import { Link } from "react-router-dom";
import {
  ArrowRight, BarChart3, Building2, ClipboardList, Compass, CreditCard, FileText, GraduationCap,
  LayoutDashboard, Target, UserPlus, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LEARNER_STEPS = [
  { icon: UserPlus, title: "Create your account", body: "Sign up with your name, email and password, and verify your email." },
  { icon: Compass, title: "Choose \u201cI Want to Learn\u201d", body: "Pick the pathway for people building skills, careers or personal interests." },
  { icon: ClipboardList, title: "Tell us about you", body: "Share your situation, background, skills, goals and how you like to learn. Skip anything optional and update it later." },
  { icon: LayoutDashboard, title: "Get your personalised dashboard", body: "See your learner profile, the skills to develop and recommended courses and learning programmes." },
  { icon: CreditCard, title: "Choose how to learn", body: "Buy a single course, a discounted programme package or a continuous learning subscription. See the price and what's included before you commit." },
  { icon: GraduationCap, title: "Learn, practise and track progress", body: "Open the courses you're entitled to, complete lessons, assessments and projects, earn certificates and get updated recommendations." },
];

const ORG_STEPS = [
  { icon: UserPlus, title: "Create your account", body: "Sign up with your name, email and password, and verify your email." },
  { icon: Building2, title: "Choose \u201cFor Organisations\u201d", body: "The pathway for businesses, institutions and teams developing their people." },
  { icon: Users, title: "Set up your organisation", body: "Tell us about your organisation, training goals, team structure, skills needs, seats and budget." },
  { icon: Target, title: "Get a recommended learning plan", body: "Receive a suggested arrangement, from a small-team plan to a department-wide programme or a customised enterprise arrangement." },
  { icon: FileText, title: "Choose your learning contract", body: "Select a contract duration that suits you. Price, seats, included features, renewal and payment terms are shown before you commit." },
  { icon: BarChart3, title: "Invite, assign and monitor", body: "Invite employees, create groups and cohorts, assign courses and track completion, assessments and skills progress." },
];

const Arrow = ({ className }) => (
  <span className={`pointer-events-none absolute -right-[1.15rem] top-1/2 z-10 -translate-y-1/2 place-items-center rounded-full border border-indigo-200 bg-white p-0.5 ${className}`} aria-hidden="true">
    <ArrowRight className="h-3.5 w-3.5 text-[#222d99]" strokeWidth={1.5} />
  </span>
);

function Steps({ steps, prefix }) {
  return (
    <ol className="relative mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3" data-testid={`${prefix}-steps`}>
      <span className="absolute bottom-10 left-10 top-10 w-px bg-indigo-200 md:hidden" aria-hidden="true" />
      {steps.map(({ icon: Icon, title, body }, i) => (
        <li key={title} className="relative rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={`${prefix}-step-${i + 1}`}>
          <div className="flex items-center justify-between">
            <span className="relative z-10 grid h-10 w-10 place-items-center rounded-full bg-[#222d99] font-display text-base font-extrabold text-white" aria-hidden="true">{i + 1}</span>
            <Icon className="h-6 w-6 text-[#222d99]" strokeWidth={1.5} aria-hidden="true" />
          </div>
          <h4 className="mt-4 text-base font-semibold text-slate-900"><span className="sr-only">Step {i + 1}: </span>{title}</h4>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-700">{body}</p>
          {i % 2 === 0 && i < steps.length - 1 && <Arrow className="hidden md:grid lg:hidden" />}
          {i % 3 !== 2 && i < steps.length - 1 && <Arrow className="hidden lg:grid" />}
        </li>
      ))}
    </ol>
  );
}

const btnCls = "rounded-full bg-[#222d99] px-7 hover:bg-[#1a237e] focus-visible:ring-2 focus-visible:ring-[#222d99] focus-visible:ring-offset-2";

export function HowItWorks() {
  return (
    <section id="how-it-works" className="scroll-mt-20 border-b border-slate-200" data-testid="how-it-works">
      <div className="mx-auto max-w-6xl px-4 pt-16 sm:px-6 lg:pt-24">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">How it works</h2>
        <p className="mt-2 text-base text-slate-700 md:text-lg">One account, two pathways.</p>
      </div>
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6" data-testid="how-learners">
        <h3 className="text-xl font-semibold text-slate-900">For individual learners</h3>
        <Steps steps={LEARNER_STEPS} prefix="how-learner" />
        <Button asChild size="lg" className={`mt-8 ${btnCls}`} data-testid="how-learn-btn"><Link to="/sign-up">I Want to Learn</Link></Button>
      </div>
      <div className="border-t border-slate-200 bg-[#F8FAFC]" data-testid="how-organisations">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:pb-24">
          <h3 className="text-xl font-semibold text-slate-900">For organisations</h3>
          <Steps steps={ORG_STEPS} prefix="how-org" />
          <Button asChild size="lg" className={`mt-8 ${btnCls}`} data-testid="how-org-btn"><Link to="/sign-up">For Organisations</Link></Button>
          <p className="mt-4 text-sm text-slate-700" data-testid="how-org-note">Already have a Crowlearn account? Individuals can join an organisation's programme with their existing account.</p>
        </div>
      </div>
    </section>
  );
}
